import { useState, type FormEvent, type ReactNode } from 'react';

/**
 * The sign-in form, shared by the portal login and the platform console.
 *
 * PRESENTATION AND FORM STATE ONLY. It does not know which API it is
 * talking to, does not import an API client, and does not store a token -
 * the caller passes `onSubmit` and owns everything behind it.
 *
 * That line matters. The two identity systems (tenant `accounts` and
 * `system_admins`) deliberately share no authentication code: separate
 * endpoints, separate secrets, separate token storage. Sharing the markup
 * is free, because markup cannot leak a session. Sharing a submit handler
 * would not be, so this component never acquires one.
 */

export interface LoginFormProps {
  title: string;
  subtitle: string;
  /** Small print under the card. */
  footnote?: ReactNode;
  /**
   * The key the API uses for the identifier inside a 422 `details` block,
   * so per-field errors attach to the right input. The tenant API kept its
   * historical wire name `username` (it holds an email); the console uses
   * `email`.
   */
  identifierField?: 'username' | 'email';
  /** Throw to show an error; resolve to let the caller redirect. */
  onSubmit: (identifier: string, password: string) => Promise<void>;
  /**
   * An optional chance to word a particular failure better than the API
   * did. Return null to fall through to the standard handling.
   */
  describeError?: (error: unknown) => string | null;
}

/** The shape both ApiError and ConsoleError share. Duck-typed on purpose:
 *  this component imports neither, so it stays free of both clients. */
interface ErrorLike {
  code?: unknown;
  message?: unknown;
  details?: unknown;
}

export function LoginForm({
  title, subtitle, footnote, identifierField = 'username', onSubmit, describeError,
}: LoginFormProps) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /** A whole-form problem: wrong credentials, suspended account, server down. */
  const [formError, setFormError] = useState<string | null>(null);
  /** Per-field problems, from the API's 422 details block. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSubmitting(true);

    try {
      await onSubmit(identifier.trim(), password);
      /*
       * No setSubmitting(false) on success. The caller redirects, which
       * unmounts this component - setting state afterwards would be a
       * setState on an unmounted component.
       */
    } catch (error) {
      applyError(error);
      setSubmitting(false);
    }
  }

  function applyError(error: unknown): void {
    const custom = describeError?.(error);
    if (custom) {
      setFormError(custom);
      return;
    }

    if (typeof error !== 'object' || error === null || !('message' in error)) {
      setFormError('Something went wrong. Please try again.');
      return;
    }

    const err = error as ErrorLike;

    if (err.code === 'VALIDATION_ERROR' && err.details && typeof err.details === 'object') {
      // Keyed by location: { "body.username": ["Email is required"] }
      const mapped: Record<string, string> = {};
      for (const [key, messages] of Object.entries(err.details as Record<string, unknown>)) {
        const field = key.replace(/^body\./, '');
        if (Array.isArray(messages) && typeof messages[0] === 'string') {
          mapped[field] = messages[0];
        }
      }
      setFieldErrors(mapped);
      // Only show a banner if the errors could not be attached to a field.
      if (Object.keys(mapped).length === 0) setFormError(String(err.message));
      return;
    }

    /*
     * The API already writes these for a human - "Invalid email or
     * password", "This account is suspended" - so pass them through rather
     * than inventing a second vocabulary on the client.
     */
    setFormError(String(err.message));
  }

  const inputClass = (field: string) =>
    [
      'w-full rounded-md border px-3 py-2 text-sm text-slate-900 shadow-sm outline-none',
      'placeholder:text-slate-400 disabled:bg-slate-50 disabled:text-slate-500',
      fieldErrors[field]
        ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100'
        : 'border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
    ].join(' ');

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
        </header>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          noValidate
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
        >
          {/* role="alert" makes a screen reader announce the message when it
              appears, which is the whole point of an error a user cannot see. */}
          {formError !== null && (
            <div
              role="alert"
              className="whitespace-pre-line rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {formError}
            </div>
          )}

          <div>
            <label htmlFor={identifierField} className="mb-1 block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id={identifierField}
              name={identifierField}
              type="email"
              // Tells password managers which field is which.
              autoComplete="email"
              autoFocus
              required
              disabled={submitting}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              aria-invalid={fieldErrors[identifierField] !== undefined}
              aria-describedby={fieldErrors[identifierField] ? `${identifierField}-error` : undefined}
              className={inputClass(identifierField)}
              placeholder="you@company.com"
            />
            {fieldErrors[identifierField] && (
              <p id={`${identifierField}-error`} className="mt-1 text-xs text-red-600">
                {fieldErrors[identifierField]}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                disabled={submitting}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={fieldErrors.password !== undefined}
                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                className={`${inputClass('password')} pr-16`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((shown) => !shown)}
                disabled={submitting}
                className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {fieldErrors.password && (
              <p id="password-error" className="mt-1 text-xs text-red-600">
                {fieldErrors.password}
              </p>
            )}
          </div>

          {/* Disabled while in flight so an impatient double-click cannot fire
              two logins - and so the button itself reports progress. */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {submitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {footnote && <p className="mt-4 text-center text-xs text-slate-500">{footnote}</p>}
      </div>
    </div>
  );
}
